import React from 'react';

import { Flex, Text } from '@lucidhq/lucidium';
import { useFormikContext } from '@lucidhq/lucidium/components/FormV2';

import { useCampaignManager } from '~/components/CampaignManager/CampaignManagerProvider';
import { usePermissions } from '~/components/Permissions/PermissionsProvider';
import { IM_FLAGS } from '~/impact-measurement/constants';
import { useSplit } from '~/impact-measurement/hooks';
import { CampaignFormValues, Channel } from '~/impact-measurement/pages/types';

import { DataPartnerField } from './DataPartnerField';
import { ExpectedMediaChannelsSection } from './ExpectedMediaChannelsSection';
import { getTrackedMediaStates } from './helpers';
import { SocialPlatformsFields } from './SocialPlatformsFields';
import { TrackedMediaItem } from './TrackedMediaItem';

type TrackedMediaSectionProps = {
  showImpressionsFields?: boolean; // TODO: this can possibly be removed determined on how we use this in the future for custom campaigns
  limitedEdit?: boolean;
};

export const TrackedMediaSection: React.FC<TrackedMediaSectionProps> = ({
  showImpressionsFields = true,
  limitedEdit = false
}) => {
  const { setFieldValue, values, errors } = useFormikContext<
    CampaignFormValues
  >();
  const { isSoftware } = useCampaignManager();

  const { isAdmin } = usePermissions();
  const { isOn } = useSplit(IM_FLAGS.CTV_TOGGLE);

  const {
    channels,
    draft_settings: { data_partners }
  } = values;

  const expected_impressions_count =
    values.expected_impressions_count ?? ({} as Record<Channel, number>);

  const {
    digital: digitalImpressions,
    linear_tv: tvImpressions,
    social: socialImpressions
  } = expected_impressions_count;

  const { isChecked } = getTrackedMediaStates(
    channels,
    digitalImpressions,
    tvImpressions,
    socialImpressions
  );

  return (
    <Flex lss={{ flexDirection: 'column', my: 'md' }}>
      <Flex lss={{ flexDirection: 'column', gap: '2px' }}>
        <TrackedMediaItem
          channels={channels}
          type="digital"
          setFieldValue={setFieldValue}
          isChecked={isChecked.digital}
          impressions={digitalImpressions}
          showImpressionsField={showImpressionsFields && isChecked.digital}
          readOnly={limitedEdit}
        />
        {isOn && (
          <ExpectedMediaChannelsSection
            isDigitalChecked={isChecked.digital}
            isReadOnly={limitedEdit}
          />
        )}
        <TrackedMediaItem
          channels={channels}
          type="linear_tv"
          setFieldValue={setFieldValue}
          isChecked={isChecked.linear_tv}
          impressions={tvImpressions}
          showImpressionsField={showImpressionsFields && isChecked.linear_tv}
          readOnly={limitedEdit}
        />
        <DataPartnerField
          name="draft_settings.data_partners"
          isLinearTvChecked={isChecked.linear_tv}
          dataPartners={data_partners}
          readOnly={limitedEdit}
        />
        {isAdmin && (
          <>
            <TrackedMediaItem
              channels={channels}
              type="social"
              setFieldValue={setFieldValue}
              isChecked={isChecked.social}
              impressions={socialImpressions}
              showImpressionsField={showImpressionsFields && isChecked.social}
              readOnly={limitedEdit}
            />
            {isSoftware && (
              <SocialPlatformsFields
                isSocialChecked={isChecked.social}
                readOnly={limitedEdit}
              />
            )}
          </>
        )}
      </Flex>

      {!!errors.channels && (
        <Text lss={{ textScale: 'control', color: 'danger500', mb: 'md' }}>
          {errors.channels}
        </Text>
      )}
    </Flex>
  );
};
